import React from 'react';
import { InternalError, isDefined, silenceError } from '@nfctron/nexus-core';
import { parse, type ElementNode } from 'svg-parser';

import type { SectorMapTypes } from '../SectorMap.types';

export const useSvgParser = (
	drawing: string,
	width: number,
	height: number,
	background: string,
	forceBackground: boolean = false,
): { parsedData: SectorMapTypes.ParsedSvgData | null; error: Error | null } =>
	React.useMemo(() => {
		try {
			const parsed = parse(drawing);
			const root = parsed.children.find((child) => child.type === 'element' && child.tagName === 'svg') as ElementNode;

			if (!isDefined(root)) {
				throw new Error('Invalid SVG: Root element not found');
			}

			const widthProp = silenceError(() => root.properties?.width ?? (root.properties?.viewBox as string)?.split(' ')[2], null);
			const heightProp = silenceError(() => root.properties?.height ?? (root.properties?.viewBox as string)?.split(' ')[3], null);
			const fillProp = root.properties?.fill?.toString();

			return {
				parsedData: {
					root,
					baseWidth: silenceError(() => parseInt(widthProp!.toString()), width),
					baseHeight: silenceError(() => parseInt(heightProp!.toString()), height),
					background: forceBackground || fillProp === 'none' ? background : fillProp!,
				},
				error: null,
			};
		} catch (error) {
			return {
				parsedData: null,
				error: error instanceof Error ? error : new InternalError('parse_error', 'Failed to parse SVG'),
			};
		}
	}, [drawing, width, height, background, forceBackground]);
